import { useState } from "react";
import { CheckGrid, FileInput, SubmitButton, SuccessBox, postMultipart, inputCls, labelCls, toast } from "@/components/formkit";

const TYPES = [
  "Prime Contractor", "National Installation Provider", "General Contractor", "Managed Service Provider",
  "Telecommunications Company", "Data Center Contractor", "Property Management Company", "Other",
];
const SERVICES = [
  "TV Mounting", "Audio / Video", "Structured Cabling", "Fiber Optic Installation", "Fiber Splicing / Testing",
  "Data Center Infrastructure", "CCTV / Security Cameras", "Smart Home Technology", "Networking / Wi-Fi",
  "Commercial Low Voltage", "Solar Installation Support", "Residential AI Integration", "Commercial AI Automation",
  "Private AI Deployment", "Enterprise AI Infrastructure", "AI Server Installation", "AI Consulting",
  "Intelligent Building Integration", "Licensed Electrical Contractor", "Solar Installation Company",
  "Solar Equipment Supplier", "Solar Project Subcontractor", "Solar General Contractor", "Other",
];

const CONFIRM = `Thank you for considering DuroTech28 as your technology services partner.

Your inquiry has been received. We will review your project requirements and contact you regarding potential partnership opportunities.`;

const EMPTY = {
  company_name: "", contact_person: "", job_title: "", business_email: "", phone: "",
  website: "", headquarters: "", partnership_type: "", services_requested: [],
  project_location: "", start_date: "", engagement: "", scope: "", technicians_needed: "",
  description: "", budget: "",
};

export default function PartnerForm() {
  const [form, setForm] = useState(EMPTY);
  const [documents, setDocuments] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleService = (opt) =>
    setForm((f) => ({
      ...f,
      services_requested: f.services_requested.includes(opt)
        ? f.services_requested.filter((x) => x !== opt)
        : [...f.services_requested, opt],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await postMultipart("/applications/partnership", form, { documents });
      setDone(true);
      setForm(EMPTY);
      setDocuments(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return <SuccessBox message={CONFIRM} onReset={() => setDone(false)} testid="partner-success" />;

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="partner-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={labelCls}>Company Name *</label>
          <input required value={form.company_name} onChange={set("company_name")} data-testid="partner-company" className={inputCls} /></div>
        <div><label className={labelCls}>Contact Person *</label>
          <input required value={form.contact_person} onChange={set("contact_person")} data-testid="partner-contact" className={inputCls} /></div>
        <div><label className={labelCls}>Job Title</label>
          <input value={form.job_title} onChange={set("job_title")} data-testid="partner-title" className={inputCls} /></div>
        <div><label className={labelCls}>Business Email *</label>
          <input required type="email" value={form.business_email} onChange={set("business_email")} data-testid="partner-email" className={inputCls} /></div>
        <div><label className={labelCls}>Phone Number *</label>
          <input required value={form.phone} onChange={set("phone")} data-testid="partner-phone" className={inputCls} /></div>
        <div><label className={labelCls}>Company Website</label>
          <input value={form.website} onChange={set("website")} data-testid="partner-website" className={inputCls} /></div>
        <div className="sm:col-span-2"><label className={labelCls}>Headquarters Location</label>
          <input value={form.headquarters} onChange={set("headquarters")} data-testid="partner-hq" className={inputCls} /></div>
      </div>

      <div>
        <span className={labelCls}>Partnership Type</span>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {TYPES.map((t) => (
            <button key={t} type="button"
              onClick={() => setForm((f) => ({ ...f, partnership_type: f.partnership_type === t ? "" : t }))}
              data-testid={`partner-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`px-3 py-2 rounded border text-left text-[11px] font-mono transition-colors ${
                form.partnership_type === t
                  ? "bg-[#d4af37]/15 border-[#d4af37] text-[#d4af37]"
                  : "bg-[#0a0a0c] border-zinc-700 text-zinc-400 hover:border-zinc-500"
              }`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <div>
        <span className={labelCls}>Services Requested — select all that apply</span>
        <CheckGrid options={SERVICES} values={form.services_requested} onToggle={toggleService} testPrefix="partner-svc" />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={labelCls}>Project Location</label>
          <input value={form.project_location} onChange={set("project_location")} data-testid="partner-location" className={inputCls} /></div>
        <div><label className={labelCls}>Estimated Start Date</label>
          <input value={form.start_date} onChange={set("start_date")} data-testid="partner-start" className={inputCls} /></div>
        <div>
          <span className={labelCls}>One-Time or Ongoing Partnership</span>
          <div className="flex gap-2">
            {["One-Time", "Ongoing"].map((v) => (
              <button key={v} type="button"
                onClick={() => setForm((f) => ({ ...f, engagement: v }))}
                data-testid={`partner-engagement-${v.toLowerCase()}`}
                className={`px-5 py-2 rounded border text-[11px] font-mono transition-colors ${
                  form.engagement === v
                    ? "bg-[#d4af37]/15 border-[#d4af37] text-[#d4af37]"
                    : "bg-[#0a0a0c] border-zinc-700 text-zinc-400 hover:border-zinc-500"
                }`}>
                {v}
              </button>
            ))}
          </div>
        </div>
        <div><label className={labelCls}>Estimated Project Scope</label>
          <input value={form.scope} onChange={set("scope")} placeholder="e.g. 40-drop office build-out" data-testid="partner-scope" className={inputCls} /></div>
        <div><label className={labelCls}>Number of Technicians Needed</label>
          <input value={form.technicians_needed} onChange={set("technicians_needed")} data-testid="partner-techs" className={inputCls} /></div>
        <div><label className={labelCls}>Estimated Budget (optional)</label>
          <input value={form.budget} onChange={set("budget")} data-testid="partner-budget" className={inputCls} /></div>
        <div className="sm:col-span-2"><label className={labelCls}>Project Description</label>
          <textarea rows="4" value={form.description} onChange={set("description")} data-testid="partner-description" className={inputCls}></textarea></div>
      </div>

      <FileInput label="Scope of work / project documents (optional)" file={documents} onChange={setDocuments} testid="partner-documents" />

      <SubmitButton submitting={submitting} label="Submit Partnership Inquiry" testid="partner-submit" />
    </form>
  );
}
